import * as i0 from '@angular/core';
import { EventEmitter, Component, ChangeDetectionStrategy, Input, Output, HostBinding, NgModule } from '@angular/core';
import * as i1 from '@taiga-ui/cdk';
import { TuiMonth, TUI_FIRST_DAY, TUI_LAST_DAY, ALWAYS_FALSE_HANDLER, TuiMonthRange, TuiYear, tuiInRange, TuiHoveredModule, TuiPressedModule, TuiRepeatTimesModule, TuiLetModule } from '@taiga-ui/cdk';
import { TuiInteractiveState, TuiRangeState } from '@taiga-ui/core/enums';
import * as i2 from '@taiga-ui/core/directives/scroll-into-view';
import { TuiScrollIntoViewModule } from '@taiga-ui/core/directives/scroll-into-view';
function TuiPrimitiveYearPickerComponent_div_0_ng_container_1_div_1_Template(rf, ctx) {
  if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 4);
    i0.ɵɵlistener("click", function TuiPrimitiveYearPickerComponent_div_0_ng_container_1_div_1_Template_div_click_0_listener() {
      const item_r2 = i0.ɵɵrestoreView(_r1).tuiLet;
      const ctx_r2 = i0.ɵɵnextContext(3);
      return i0.ɵɵresetView(ctx_r2.onItemClick(item_r2));
    })("tuiHoveredChange", function TuiPrimitiveYearPickerComponent_div_0_ng_container_1_div_1_Template_div_tuiHoveredChange_0_listener($event) {
      const item_r2 = i0.ɵɵrestoreView(_r1).tuiLet;
      const ctx_r2 = i0.ɵɵnextContext(3);
      return i0.ɵɵresetView(ctx_r2.onItemHovered($event, item_r2));
    })("tuiPressedChange", function TuiPrimitiveYearPickerComponent_div_0_ng_container_1_div_1_Template_div_tuiPressedChange_0_listener($event) {
      const item_r2 = i0.ɵɵrestoreView(_r1).tuiLet;
      const ctx_r2 = i0.ɵɵnextContext(3);
      return i0.ɵɵresetView(ctx_r2.onItemPressed($event, item_r2));
    });
    i0.ɵɵelementStart(1, "div", 5);
    i0.ɵɵtext(2);
    i0.ɵɵelementEnd()();
  }
  if (rf & 2) {
    const item_r2 = ctx.tuiLet;
    const ctx_r2 = i0.ɵɵnextContext(3);
    i0.ɵɵclassProp("t-cell_interval", ctx_r2.itemIsInterval(item_r2))("t-cell_today", ctx_r2.itemIsToday(item_r2));
    i0.ɵɵproperty("tuiScrollIntoView", ctx_r2.scrollItemIntoView(item_r2));
    i0.ɵɵattribute("data-range", ctx_r2.getItemRange(item_r2))("data-state", ctx_r2.getItemState(item_r2));
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate(item_r2);
  }
}
function TuiPrimitiveYearPickerComponent_div_0_ng_container_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, TuiPrimitiveYearPickerComponent_div_0_ng_container_1_div_1_Template, 3, 8, "div", 3);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const colIndex_r4 = ctx.$implicit;
    const rowIndex_r5 = i0.ɵɵnextContext().$implicit;
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵproperty("tuiLet", ctx_r2.getItem(rowIndex_r5, colIndex_r4));
  }
}
function TuiPrimitiveYearPickerComponent_div_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 1);
    i0.ɵɵtemplate(1, TuiPrimitiveYearPickerComponent_div_0_ng_container_1_Template, 2, 1, "ng-container", 2);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    i0.ɵɵadvance();
    i0.ɵɵproperty("tuiRepeatTimesOf", 4);
  }
}
const LIMIT = 100;
const ITEMS_IN_ROW = 4;
class TuiPrimitiveYearPickerComponent {
  constructor() {
    this.hoveredItem = null;
    this.pressedItem = null;
    this.currentYear = TuiMonth.currentLocal().year;
    this.value = null;
    this.initialItem = TuiMonth.currentLocal();
    this.min = TUI_FIRST_DAY;
    this.max = TUI_LAST_DAY;
    this.disabledItemHandler = ALWAYS_FALSE_HANDLER;
    this.yearClick = new EventEmitter();
  }
  get computedMin() {
    var _a;
    return (_a = this.min) !== null && _a !== void 0 ? _a : TUI_FIRST_DAY;
  }
  get computedMax() {
    var _a;
    return (_a = this.max) !== null && _a !== void 0 ? _a : TUI_LAST_DAY;
  }
  get isSingle() {
    return this.isRange(this.value) && this.value.from.yearSame(this.value.to);
  }
  get rows() {
    return Math.ceil((this.calculatedMax - this.calculatedMin) / ITEMS_IN_ROW);
  }
  get calculatedMin() {
    const initial = this.initialItem.year - LIMIT;
    const min = this.computedMin;
    return min.year > initial ? min.year : initial;
  }
  get calculatedMax() {
    const initial = this.initialItem.year + LIMIT;
    const max = this.computedMax;
    return max.year < initial ? max.year + 1 : initial;
  }
  isRange(item) {
    return item instanceof TuiMonthRange;
  }
  scrollItemIntoView(item) {
    return this.initialItem.year === item;
  }
  getItem(rowIndex, colIndex) {
    return rowIndex * ITEMS_IN_ROW + colIndex + this.calculatedMin;
  }
  getItemState(item) {
    const {
      disabledItemHandler,
      pressedItem,
      hoveredItem
    } = this;
    const max = this.computedMax;
    if (max.year < item || disabledItemHandler !== ALWAYS_FALSE_HANDLER && disabledItemHandler(item)) {
      return TuiInteractiveState.Disabled;
    }
    if (pressedItem === item) {
      return TuiInteractiveState.Active;
    }
    if (hoveredItem === item) {
      return TuiInteractiveState.Hover;
    }
    return null;
  }
  getItemRange(item) {
    const {
      value,
      hoveredItem
    } = this;
    if (value === null) {
      return null;
    }
    if (value instanceof TuiYear) {
      return value.year === item ? TuiRangeState.Single : null;
    }
    if (!(value instanceof TuiMonthRange)) {
      return value.find(day => day.year === item) ? TuiRangeState.Single : null;
    }
    if (value.from.year === item && !value.from.yearSame(value.to) || hoveredItem !== null && hoveredItem > value.from.year && value.from.year === item && value.from.yearSame(value.to) || hoveredItem !== null && hoveredItem === item && hoveredItem < value.from.year && value.from.yearSame(value.to)) {
      return TuiRangeState.Start;
    }
    if (value.to.year === item && !value.from.yearSame(value.to) || hoveredItem !== null && hoveredItem < value.from.year && value.from.year === item && value.from.yearSame(value.to) || hoveredItem !== null && hoveredItem === item && hoveredItem > value.from.year && value.from.yearSame(value.to)) {
      return TuiRangeState.End;
    }
    return value.from.yearSame(value.to) && value.from.year === item ? TuiRangeState.Single : null;
  }
  itemIsToday(item) {
    return this.currentYear === item;
  }
  itemIsInterval(item) {
    const {
      value,
      hoveredItem
    } = this;
    if (value === null || !this.isRange(value)) {
      return false;
    }
    if (!value.from.yearSame(value.to)) {
      return value.from.year <= item && value.to.year > item;
    }
    if (hoveredItem === null || value.from.year === hoveredItem) {
      return false;
    }
    return tuiInRange(item, Math.min(value.from.year, hoveredItem), Math.max(value.from.year, hoveredItem));
  }
  onItemHovered(hovered, item) {
    this.updateHoveredItem(hovered, item);
  }
  onItemPressed(pressed, item) {
    this.updatePressedItem(pressed, item);
  }
  onItemClick(item) {
    this.yearClick.emit(new TuiYear(item));
  }
  updateHoveredItem(hovered, item) {
    this.hoveredItem = hovered ? item : null;
  }
  updatePressedItem(pressed, item) {
    this.pressedItem = pressed ? item : null;
  }
}
TuiPrimitiveYearPickerComponent.ɵfac = function TuiPrimitiveYearPickerComponent_Factory(t) {
  return new (t || TuiPrimitiveYearPickerComponent)();
};
TuiPrimitiveYearPickerComponent.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
  type: TuiPrimitiveYearPickerComponent,
  selectors: [["tui-primitive-year-picker"]],
  hostVars: 2,
  hostBindings: function TuiPrimitiveYearPickerComponent_HostBindings(rf, ctx) {
    if (rf & 2) {
      i0.ɵɵclassProp("_single", ctx.isSingle);
    }
  },
  inputs: {
    value: "value",
    initialItem: "initialItem",
    min: "min",
    max: "max",
    disabledItemHandler: "disabledItemHandler"
  },
  outputs: {
    yearClick: "yearClick"
  },
  decls: 1,
  vars: 1,
  consts: [["automation-id", "tui-primitive-year-picker__row", "class", "t-row", 4, "tuiRepeatTimes", "tuiRepeatTimesOf"], ["automation-id", "tui-primitive-year-picker__row", 1, "t-row"], [4, "tuiRepeatTimes", "tuiRepeatTimesOf"], ["automation-id", "tui-primitive-year-picker__cell", "class", "t-cell", 3, "t-cell_interval", "t-cell_today", "tuiScrollIntoView", "click", "tuiHoveredChange", "tuiPressedChange", 4, "tuiLet"], ["automation-id", "tui-primitive-year-picker__cell", 1, "t-cell", 3, "click", "tuiHoveredChange", "tuiPressedChange", "tuiScrollIntoView"], [1, "t-item"]],
  template: function TuiPrimitiveYearPickerComponent_Template(rf, ctx) {
    if (rf & 1) {
      i0.ɵɵtemplate(0, TuiPrimitiveYearPickerComponent_div_0_Template, 2, 1, "div", 0);
    }
    if (rf & 2) {
      i0.ɵɵproperty("tuiRepeatTimesOf", ctx.rows);
    }
  },
  dependencies: [i1.TuiRepeatTimesDirective, i1.TuiLetDirective, i2.TuiScrollIntoViewDirective, i1.TuiHoveredDirective, i1.TuiPressedDirective],
  styles: ["[_nghost-%COMP%]{display:block;font:var(--tui-font-text-m)}.t-row[_ngcontent-%COMP%]{position:relative;display:flex;justify-content:space-between;height:2.25rem;isolation:isolate}.t-item[_ngcontent-%COMP%]{position:relative;flex:1;line-height:2rem;border-radius:var(--tui-radius-m)}.t-item[_ngcontent-%COMP%]:before, .t-item[_ngcontent-%COMP%]:after{content:\"\";position:absolute;top:0;left:0;right:0;bottom:0;z-index:-1;border-radius:var(--tui-radius-m)}.t-item[_ngcontent-%COMP%]:after{border-radius:.5rem}.t-cell[_ngcontent-%COMP%]{position:relative;display:flex;align-items:center;justify-content:center;width:3.9375rem;text-align:center;outline:none;cursor:pointer;background-clip:content-box;box-sizing:border-box;border:.125rem solid transparent}.t-cell[_ngcontent-%COMP%]:before{content:\"\";position:absolute;top:0;left:0;right:0;bottom:0;z-index:-1;border-radius:var(--tui-radius-m)}.t-cell_today[_ngcontent-%COMP%]:after{position:absolute;left:50%;transform:translate(-50%);content:\"\";bottom:.3125rem;height:.125rem;width:.75rem;border-radius:.375rem;background-color:var(--tui-text-01)}.t-cell_interval[_ngcontent-%COMP%]:before{background:var(--tui-base-02)}._single[_nghost-%COMP%]   .t-cell_interval[_ngcontent-%COMP%]:before{background:var(--tui-secondary-hover)}.t-cell_interval[_ngcontent-%COMP%]:not(:last-child):before{right:-.25rem;border-top-right-radius:0;border-bottom-right-radius:0}.t-cell_interval[_ngcontent-%COMP%]:not([data-range=\"start\"]):not(:first-child):before{border-top-left-radius:0;border-bottom-left-radius:0}.t-cell_interval[_ngcontent-%COMP%]:last-child:first-child:before{right:0}.t-cell_interval[_ngcontent-%COMP%]:first-child > .t-item[_ngcontent-%COMP%]{border-top-left-radius:var(--tui-radius-m);border-bottom-left-radius:var(--tui-radius-m)}.t-cell_interval[_ngcontent-%COMP%]:last-child > .t-item[_ngcontent-%COMP%]{border-top-right-radius:var(--tui-radius-m);border-bottom-right-radius:var(--tui-radius-m)}.t-cell_interval[_ngcontent-%COMP%] > .t-item[_ngcontent-%COMP%]{border-radius:0}.t-cell[data-range][_ngcontent-%COMP%]:after{background-color:var(--tui-primary-text)}.t-cell[data-range][_ngcontent-%COMP%] > .t-item[_ngcontent-%COMP%]{color:var(--tui-primary-text)}.t-cell[data-range][_ngcontent-%COMP%] > .t-item[_ngcontent-%COMP%]:before, .t-cell[data-range][_ngcontent-%COMP%] > .t-item[_ngcontent-%COMP%]:after{background-color:var(--tui-primary)}.t-cell[data-range][data-state=hover][_ngcontent-%COMP%] > .t-item[_ngcontent-%COMP%]:before, .t-cell[data-range][data-state=hover][_ngcontent-%COMP%] > .t-item[_ngcontent-%COMP%]:after{background-color:var(--tui-primary-hover)}.t-cell[data-range][data-state=active][_ngcontent-%COMP%] > .t-item[_ngcontent-%COMP%]:before, .t-cell[data-range][data-state=active][_ngcontent-%COMP%] > .t-item[_ngcontent-%COMP%]:after{background-color:var(--tui-primary-active)}.t-cell[data-range=end][_ngcontent-%COMP%]:before{background:var(--tui-base-02)}._single[_nghost-%COMP%]   .t-cell[data-range=end][_ngcontent-%COMP%]:before{background:var(--tui-secondary-hover)}.t-cell[data-range=end][_ngcontent-%COMP%]:not(:first-child):before{border-top-left-radius:0;border-bottom-left-radius:0}.t-cell[data-range=end][_ngcontent-%COMP%] > .t-item[_ngcontent-%COMP%]:before{left:.625rem;border-top-left-radius:0;border-bottom-left-radius:0}.t-cell[data-range=end][_ngcontent-%COMP%] > .t-item[_ngcontent-%COMP%]:after{left:-2rem;right:100%;transform:translate(1.6rem) scaleY(.83) scaleX(.5) rotate(45deg)}.t-cell[data-range=start][_ngcontent-%COMP%] > .t-item[_ngcontent-%COMP%]:before{right:.625rem;border-top-right-radius:0;border-bottom-right-radius:0}.t-cell[data-range=start][_ngcontent-%COMP%] > .t-item[_ngcontent-%COMP%]:after{left:100%;right:-2rem;transform:translate(-1.6rem) scaleY(.83) scaleX(.5) rotate(45deg)}.t-cell[data-range=single][_ngcontent-%COMP%] > .t-item[_ngcontent-%COMP%]:after{display:none}.t-cell[data-state=disabled][_ngcontent-%COMP%]{pointer-events:none}.t-cell[data-state=disabled][_ngcontent-%COMP%] > .t-item[_ngcontent-%COMP%]{opacity:.36}.t-cell[data-state=hover][_ngcontent-%COMP%]:hover:not([data-range]) > .t-item[_ngcontent-%COMP%]{background-color:var(--tui-secondary-hover)}.t-cell[data-state=active][_ngcontent-%COMP%]:hover:not([data-range]) > .t-item[_ngcontent-%COMP%]{background-color:var(--tui-secondary-active)}[_nghost-%COMP%]{width:15.75rem}.t-row[_ngcontent-%COMP%]{margin:.875rem 0}.t-row[_ngcontent-%COMP%]:first-child{margin-top:0}.t-row[_ngcontent-%COMP%]:last-child{margin-bottom:0}"],
  changeDetection: 0
});
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TuiPrimitiveYearPickerComponent, [{
    type: Component,
    args: [{
      selector: 'tui-primitive-year-picker',
      templateUrl: './primitive-year-picker.template.html',
      styleUrls: ['./primitive-year-picker.style.less'],
      changeDetection: ChangeDetectionStrategy.OnPush
    }]
  }], null, {
    value: [{
      type: Input
    }],
    initialItem: [{
      type: Input
    }],
    min: [{
      type: Input
    }],
    max: [{
      type: Input
    }],
    disabledItemHandler: [{
      type: Input
    }],
    yearClick: [{
      type: Output
    }],
    isSingle: [{
      type: HostBinding,
      args: ['class._single']
    }]
  });
})();
class TuiPrimitiveYearPickerModule {}
TuiPrimitiveYearPickerModule.ɵfac = function TuiPrimitiveYearPickerModule_Factory(t) {
  return new (t || TuiPrimitiveYearPickerModule)();
};
TuiPrimitiveYearPickerModule.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
  type: TuiPrimitiveYearPickerModule
});
TuiPrimitiveYearPickerModule.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
  imports: [[TuiHoveredModule, TuiPressedModule, TuiRepeatTimesModule, TuiLetModule, TuiScrollIntoViewModule]]
});
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TuiPrimitiveYearPickerModule, [{
    type: NgModule,
    args: [{
      imports: [TuiHoveredModule, TuiPressedModule, TuiRepeatTimesModule, TuiLetModule, TuiScrollIntoViewModule],
      declarations: [TuiPrimitiveYearPickerComponent],
      exports: [TuiPrimitiveYearPickerComponent]
    }]
  }], null, null);
})();

/**
 * Generated bundle index. Do not edit.
 */

export { TuiPrimitiveYearPickerComponent, TuiPrimitiveYearPickerModule };
